import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import {
  PITCH_PROOF_FREE_LINE_MAX,
  PITCH_PROOF_MAX_ITEMS,
  type PitchProofChoice,
  type PitchProofItem,
} from 'fym-shared/src/music24/outreach/pitchProof';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { SavedTextarea } from '../components/SavedTextarea';
import { useCampaignActions } from '../hooks/useCampaignActions';
import { useProofOptions } from '../hooks/useProofOptions';
import { QuestionPanel } from './QuestionPanel';
import { proofRows, toggleProofKey, withFreeLine } from './proofChoice';
import { proofMessages as messages } from './proofMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the setup; each part uses its own slice of it */
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProofPickerProps {
  accountId: number;
  campaignUuid: string;
}

const LABELS = {
  streams28d: messages.streams28d,
  monthlyListeners: messages.monthlyListeners,
  followers: messages.followers,
  playlistAdds: messages.playlistAdds,
} as const;

/**
 * The proof of the Message step (pitch email v3, owner decision 2026-10-27): a checkbox for each
 * number of our data (`campaigns.proofOptions`) and one free line, at most 3 together. Each change
 * saves at once (`campaigns.update { proof }`); the inputs wait while a save runs. A chosen item
 * whose number is not in our data now still shows, so the artist can uncheck it.
 */
const ProofPickerComponent: React.FC<ProofPickerProps> = ({ accountId, campaignUuid }) => {
  const intl = useIntl();
  const { proof, isLoading } = useProofOptions(accountId, campaignUuid);
  const { updateCampaign } = useCampaignActions(accountId);
  const [failed, setFailed] = useState(false);
  // One save at a time: two fast clicks would each send a list from the same old choice.
  const [saving, setSaving] = useState(false);
  const chosen = proof?.chosen ?? [];
  const rows = proofRows(proof?.options ?? [], chosen);
  const free = chosen.find((item) => item.key === 'custom')?.value ?? '';

  const save = (next: PitchProofChoice[] | null) => {
    if (next === null || saving) {
      return;
    }
    setFailed(false);
    setSaving(true);
    updateCampaign({ campaignUuid, proof: next })
      .catch((error: unknown) => {
        console.error(error);
        setFailed(true);
      })
      .finally(() => setSaving(false));
  };

  const label = (option: PitchProofItem) =>
    option.key === 'custom' ? option.value : intl.formatMessage(LABELS[option.key], { value: option.value });

  return (
    <QuestionPanel
      title={intl.formatMessage(messages.title)}
      hint={intl.formatMessage(messages.hint, { max: PITCH_PROOF_MAX_ITEMS })}
      testId="builder-proof"
    >
      {isLoading ? (
        <LoadingView />
      ) : (
        <>
          {(proof?.options.length ?? 0) === 0 && (
            <p className="small text-muted mb-3">{intl.formatMessage(messages.none)}</p>
          )}
          {rows.length > 0 && (
            <div className={clsx(styles.options, 'mb-3')} aria-busy={saving}>
              {rows.map(({ item: option, stale }) => {
                const on = chosen.some((item) => item.key === option.key);
                const full = !on && chosen.length >= PITCH_PROOF_MAX_ITEMS;
                return (
                  <button
                    key={option.key}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    disabled={full || saving}
                    title={full ? intl.formatMessage(messages.full, { max: PITCH_PROOF_MAX_ITEMS }) : undefined}
                    className={clsx(styles.pickable, styles.option, on && styles.picked)}
                    onClick={() => save(toggleProofKey(chosen, option))}
                  >
                    <span className={styles.check} />
                    <span>
                      <b>{label(option)}</b>
                      <small>{intl.formatMessage(stale ? messages.notInData : messages.fromData)}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <SavedTextarea
            singleLine
            label={intl.formatMessage(messages.freeLabel)}
            value={free}
            onSave={(text) => save(withFreeLine(chosen, text))}
            maxLength={PITCH_PROOF_FREE_LINE_MAX}
            hint={intl.formatMessage(messages.freeLimit, { max: PITCH_PROOF_FREE_LINE_MAX })}
            disabled={saving || (free === '' && chosen.length >= PITCH_PROOF_MAX_ITEMS)}
          />
          {failed && (
            <p className="small text-danger mt-2 mb-0" role="alert">
              {intl.formatMessage(messages.failed)}
            </p>
          )}
        </>
      )}
    </QuestionPanel>
  );
};

export const ProofPicker = TypedMemo(ProofPickerComponent);
